import { useMemo, useState } from "react";
import { Check, Plus, Search, X } from "lucide-react";
import { getOrderMutationErrorMessage } from "@/features/order/errors";
import { useAddItem } from "@/features/order/hooks";
import type { EnrichedOrder } from "@/features/order/types";
import { useQuotationItems } from "@/features/quotation/hooks";
import type { EnrichedQuotationItem } from "@/features/quotation/types";
import {
  getDefaultQuantity,
  getQuantityInputMode,
  getQuantityInputStep,
  getWholeDirectKgQuantityInputAttributes,
  isDirectKgUnit,
  parsePositiveQuantityInput,
  validateWholeDirectKgQuantityInput,
} from "@/lib/quantity";
import { orderInputStyle } from "./orderStyles";

type SuperAddItemPanelProps = {
  order: EnrichedOrder;
  onClose: () => void;
  onFeedback?: (feedback: {
    title: string;
    message?: string;
    tone: "success" | "error";
  }) => void;
};

const DIRECT_KG_QUANTITY_FEEDBACK = {
  title: "Quantidade inválida",
  message:
    "Para itens vendidos por kg, informe um número inteiro maior que zero.",
  tone: "error" as const,
};

export function SuperAddItemPanel({
  order,
  onClose,
  onFeedback,
}: SuperAddItemPanelProps) {
  const [search, setSearch] = useState("");
  const [quantities, setQuantities] = useState<Record<string, string>>({});
  const [adding, setAdding] = useState<string | null>(null);
  const [addedToast, setAddedToast] = useState<string | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const addItem = useAddItem();
  const { data: quotationData } = useQuotationItems(order.quotation_id ?? "");

  const filtered = useMemo(() => {
    if (!quotationData?.items) return [] as EnrichedQuotationItem[];
    const items = quotationData.items as EnrichedQuotationItem[];
    if (!search.trim()) return items;
    const term = search.toLowerCase();
    return items.filter(
      (qi) =>
        qi.product_name?.toLowerCase().includes(term) ||
        qi.supplier_name?.toLowerCase().includes(term),
    );
  }, [quotationData, search]);

  async function handleAdd(qi: EnrichedQuotationItem) {
    const rawQuantity = quantities[qi.id] ?? "";
    const directKgValidation = isDirectKgUnit(qi.unit_type)
      ? validateWholeDirectKgQuantityInput(rawQuantity)
      : null;
    if (directKgValidation?.reason) {
      onFeedback?.(DIRECT_KG_QUANTITY_FEEDBACK);
      return;
    }
    const qty = directKgValidation
      ? directKgValidation.quantity!
      : parsePositiveQuantityInput(rawQuantity, qi.unit_type);
    if (!qty || qty <= 0) {
      setErrors((prev) => ({
        ...prev,
        [qi.id]: "Informe uma quantidade válida",
      }));
      return;
    }
    setErrors((prev) => ({ ...prev, [qi.id]: "" }));
    setAdding(qi.id);
    try {
      await addItem.mutateAsync({
        store_id: order.store_id,
        quotation_item_id: qi.id,
        quantity: qty,
      });
      setQuantities((prev) => ({ ...prev, [qi.id]: "" }));
      setAddedToast(qi.product_name ?? "Item");
      setTimeout(() => setAddedToast(null), 2500);
    } catch (e) {
      onFeedback?.({
        title: getOrderMutationErrorMessage(e),
        tone: "error",
      });
      setErrors((prev) => ({
        ...prev,
        [qi.id]: getOrderMutationErrorMessage(e),
      }));
    } finally {
      setAdding(null);
    }
  }

  return (
    <div
      style={{
        marginTop: "0.75rem",
        padding: "0.875rem",
        background: "var(--login-bg)",
        border: "1px solid var(--login-border)",
        borderRadius: "0.5rem",
        display: "flex",
        flexDirection: "column",
        gap: "0.75rem",
        position: "relative",
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <span
          style={{
            fontFamily: "'Barlow Condensed', sans-serif",
            fontWeight: 700,
            fontSize: "0.8125rem",
            color: "var(--login-text)",
            letterSpacing: "0.04em",
          }}
        >
          ADICIONAR ITEM
        </span>
        <button
          aria-label="Fechar adicionar item"
          onClick={onClose}
          style={{
            background: "none",
            border: "none",
            cursor: "pointer",
            color: "var(--login-muted)",
            display: "flex",
            padding: "0.25rem",
          }}
        >
          <X size={14} />
        </button>
      </div>
      <div style={{ position: "relative" }}>
        <Search
          size={14}
          style={{
            position: "absolute",
            left: "0.625rem",
            top: "50%",
            transform: "translateY(-50%)",
            color: "var(--login-muted)",
            pointerEvents: "none",
          }}
        />
        <input
          type="text"
          placeholder="Buscar produto ou fornecedor..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={{
            ...orderInputStyle,
            paddingLeft: "2rem",
            fontSize: "0.875rem",
            minHeight: "36px",
          }}
        />
      </div>
      {addedToast && (
        <div
          style={{
            background: "#16a34a15",
            border: "1px solid #16a34a30",
            borderRadius: "0.375rem",
            padding: "0.375rem 0.75rem",
            color: "#16a34a",
            fontFamily: "'DM Sans', sans-serif",
            fontSize: "0.8125rem",
            fontWeight: 600,
            display: "flex",
            alignItems: "center",
            gap: "0.375rem",
          }}
        >
          <Check size={14} strokeWidth={3} /> {addedToast} adicionado!
        </div>
      )}
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: "0.5rem",
          maxHeight: "300px",
          overflowY: "auto",
        }}
      >
        {filtered.length === 0 ? (
          <div
            style={{
              textAlign: "center",
              color: "var(--login-muted)",
              fontSize: "0.8125rem",
              padding: "1rem",
              fontFamily: "'DM Sans', sans-serif",
            }}
          >
            Nenhum item encontrado.
          </div>
        ) : (
          filtered.map((qi) => (
            <div
              key={qi.id}
              style={{
                display: "flex",
                alignItems: "center",
                gap: "0.5rem",
                padding: "0.5rem 0.625rem",
                background: "var(--login-surface)",
                border: "1px solid var(--login-border)",
                borderRadius: "0.375rem",
              }}
            >
              <div style={{ flex: 1, minWidth: 0 }}>
                <div
                  style={{
                    fontFamily: "'Barlow Condensed', sans-serif",
                    fontWeight: 700,
                    fontSize: "0.9375rem",
                    color: "var(--login-text)",
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  {qi.product_name ?? qi.id}
                </div>
                <div
                  style={{
                    fontSize: "0.75rem",
                    color: "var(--login-muted)",
                    fontFamily: "'DM Sans', sans-serif",
                  }}
                >
                  {qi.supplier_name} · R$ {qi.price_per_unit?.toFixed(2)}
                </div>
                {errors[qi.id] && (
                  <div
                    style={{
                      fontSize: "0.6875rem",
                      color: "#ef4444",
                      fontFamily: "'DM Sans', sans-serif",
                      marginTop: "0.125rem",
                    }}
                  >
                    {errors[qi.id]}
                  </div>
                )}
              </div>
              <input
                type="number"
                {...(isDirectKgUnit(qi.unit_type)
                  ? getWholeDirectKgQuantityInputAttributes()
                  : {
                      min: getDefaultQuantity(qi.unit_type),
                      step: getQuantityInputStep(qi.unit_type),
                      inputMode: getQuantityInputMode(qi.unit_type),
                    })}
                placeholder="Qtd"
                value={quantities[qi.id] ?? ""}
                onChange={(e) =>
                  setQuantities((prev) => ({
                    ...prev,
                    [qi.id]: e.target.value,
                  }))
                }
                style={{
                  ...orderInputStyle,
                  width: "64px",
                  minHeight: "32px",
                  padding: "0.25rem 0.375rem",
                  textAlign: "center",
                  fontSize: "0.875rem",
                }}
              />
              <button
                aria-label={`Adicionar ${qi.product_name ?? qi.id}`}
                onClick={() => handleAdd(qi)}
                disabled={adding === qi.id}
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  width: "32px",
                  height: "32px",
                  background:
                    adding === qi.id
                      ? "var(--login-border)"
                      : "var(--login-accent)",
                  border: "none",
                  borderRadius: "0.375rem",
                  color: adding === qi.id ? "var(--login-muted)" : "#fff",
                  cursor: adding === qi.id ? "not-allowed" : "pointer",
                  flexShrink: 0,
                  transition: "all 0.15s",
                }}
              >
                {adding === qi.id ? (
                  <div
                    style={{
                      width: "12px",
                      height: "12px",
                      border: "2px solid rgba(255,255,255,0.3)",
                      borderTopColor: "currentColor",
                      borderRadius: "50%",
                      animation: "spin 0.8s linear infinite",
                    }}
                  />
                ) : (
                  <Plus size={14} strokeWidth={2.5} />
                )}
              </button>
            </div>
          ))
        )}
      </div>
    </div>
  );
}
